import {
    faBrain,
    faChevronLeft,
    faChevronRight,
    faCopy,
    faExpand,
    faSearch,
    faStar,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {observer} from 'mobx-react-lite';
import * as React from 'react';
import {FunctionComponent} from 'react';

import {formatDateTime} from '../../helpers/helpers';
import {Button} from '../Button';
import {Select} from '../Select';
import {ShrinkExpandFlatDetails} from '../ShrinkExpandFlatDetails';
import {FlatListForLogGenericState} from './FlatListForLogGenericState';
import {FlatStatsGraph} from './FlatStatsGraph';
import {LogType} from '../../swagger';
import {FlatDetailsDevices} from './FlatDetailsDevices';
import {FlatDetailsStructuralData} from './FlatDetailsStructuralData';
import {FlatDetailsStackTrace, toStackLine} from './FlatDetailsStackTrace';

export interface FlatDetailsProps {
    state: FlatListForLogGenericState;
}

type LogNames = {
    [key: string]: string,
}


const logNames: Map<string, string> = new Map();
logNames.set(LogType.GenericJs, 'JavaScript');
logNames.set(LogType.AppJs, 'JavaScript');
logNames.set(LogType.WordPress, 'PHP');
logNames.set(LogType.Php, 'PHP');
logNames.set(LogType.DotnetCoreMicrosoftExtensions, 'C#');
logNames.set(LogType.DotnetLog4Net, 'C#');
logNames.set(LogType.JavaLog4J, 'Java');
logNames.set(LogType.JavaLogback, 'Java');

export const FlatDetailsComponent: FunctionComponent<FlatDetailsProps> = observer(({state}) => {
    const f = state.flat;
    if (!f || !state.log) {
        return <div/>;
    }

    // @ts-ignore
    const jsLogType: boolean = [LogType.GenericJs, LogType.AppJs].includes(state.log.logType!);

    let stackTrace = f.stackTrace?.filter(x => !x.includes('muscula.com'));
    if (stackTrace?.length === 0) {
        stackTrace = undefined;
    }

    const messageForHelpSearch = f.message ?? '';
    const expandableTitle = (f?.message?.length ?? 0) > 160;
    return (
        <div
            data-tut="flatdetailsheader">
            <section
                id="flat-details"
                className="card mb-4">
                <div className="card-header">
                    <div id="flat-details-controls" className="flex flex-wrap items-center">
                        <div data-tut="item-toggle" id="item-toggle" className="flex items-center mr-auto mb-2">
                            <button
                                disabled={state.previousFlatDisabled}
                                className="btn btn-secondary mr-px text-m-gray-6"
                                onClick={() => state.previousFlat()}
                                title={'Previous entry. You can also use arrow buttons'}
                            >
                                <FontAwesomeIcon className="fill-current" icon={faChevronLeft}/>
                            </button>
                            <button
                                disabled={state.nextFlatDisabled}
                                className="btn btn-secondary ml-px mr-3 text-m-gray-6"
                                onClick={() => state.nextFlat()}
                                title={'Next entry. You can also use arrow buttons'}
                            >
                                <FontAwesomeIcon className="fill-current" icon={faChevronRight}/>
                            </button>
                            <span className="font-roboto-medium text-base">
                                {state.currentFlatNumber} of {state.log?.total}
                            </span>
                        </div>

                        <div className="flex items-center 2xs:mr-6 mb-2">
                            <Select
                                className="inline-block"
                                id={'assignflat' + f.flatId}
                                htmlFor={'assignflat' + f.flatId}
                                label="Assigned to:"
                                options={state.currentProjectUsers}
                                onChange={(x) => state.assignCurrentFlat(x)}
                                name="flatAssignment"
                                value={f.assignedTo ?? ''}
                            />
                        </div>

                        <button
                            className="btn btn-secondary mr-6 mb-2"
                            onClick={(e) => state.ignoreFlat(e)}
                            title={'Press shift to omit confirmation'}
                        >
                            Ignore
                        </button>
                        <button
                            className="btn text-m-brand-red mb-2"
                            onClick={(e) => state.deleteFlat(e)}
                            title={'Press shift to omit confirmation'}
                        >
                            <FontAwesomeIcon className="mr-4" icon={faTrash}/>
                            Delete
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    <div className="block md:flex flex-wrap items-center mb-4">
                        <h3
                            title={expandableTitle ? 'Double click to expand' : ''}
                            onDoubleClick={() => expandableTitle ? state.toggleTruncate() : {}}

                            className={'block mb-2 xl:mb-0 md:inline-block mr-auto text-m-green w-full xl:w-9/12 ' + (expandableTitle && state.truncateMessage ? ' truncate ' : '')}
                        >                             {f.message}
                        </h3>
                        <div>
                            {expandableTitle && <Button title="Expand"
                                                        disabled={!expandableTitle}
                                                        onClick={() => state.toggleTruncate()}><FontAwesomeIcon
                                className={f.starred ? 'text-m-yellow' : ''} icon={faExpand} /></Button>}
                        </div>
                        <div className="inline-block mr-auto xl:mr-0">
                            <span className={'flat-list-item-bullet relative top-auto ' + f.severity} />
                            <span className="ml-4">{f.severity}</span>
                        </div>
                    </div>
                    <div className="mb-4">


                        <Button title="Copy to clipboard"
                                onClick={() => navigator.clipboard.writeText(f.message!)}><FontAwesomeIcon
                            className={f.starred ? 'text-m-yellow' : ''} icon={faCopy} /></Button>

                        <button
                            className="btn btn-secondary mr-6"
                            onClick={() => state.toggleStarred()}
                            title="Set / Unset starred status"
                        >
                            <FontAwesomeIcon className={f.starred ? 'text-m-yellow' : ''} icon={faStar} />
                        </button>
                        <button
                            className="btn btn-secondary mr-6"
                            onClick={() => state.aiExplain(messageForHelpSearch, logNames.get(state.log?.logType ?? LogType.AppJs) ?? 'JavaScript')}
                            title="Sends question to OpenAI"
                        >
                            <FontAwesomeIcon className="mr-4" icon={faBrain} />
                            Explain this error with help of AI.
                        </button>
                        <a href={'https://www.google.com/search?q=' + encodeURIComponent(messageForHelpSearch)}
                           className="btn btn-secondary mr-6"
                            target="_blank"
                            title="Opens Google search"
                        >
                            <FontAwesomeIcon className="mr-4" icon={faSearch}/>
                            Google for explanation
                        </a>
                        {state.explanation && <div className="card-body bg-m-gray-2 mt-2">
                            {state.explanation}
                        </div>}
                    </div>
                    <dl className="flat-detail-info grid grid-cols-12">
                        {f.className && (
                            <React.Fragment>
                                <dt className="fd-title">ClassName:</dt>
                                <dd className="fd-content break-all">{f.className}</dd>
                            </React.Fragment>
                        )}
                        {f.exception && (f.exception !== f.message) && (
                            <React.Fragment>
                                <dt className="col-span-12 lg:col-span-3 2xl:col-span-2">Exception:</dt>
                                <dd className="col-span-12 lg:col-span-9 2xl:col-span-10 h-32 bg-m-gray-2 mb-2 p-2 overflow-y-auto overflow-x">{f.exception}</dd>
                            </React.Fragment>
                        )}
                        <dt className="fd-title">Occurences:</dt>
                        <dd className="fd-content">{f.count}</dd>
                        <dt className="fd-title">Last time seen:</dt>
                        <dd className="fd-content">{formatDateTime(f.lastTimeSeen)}</dd>
                        <dt className="fd-title">First time seen:</dt>
                        <dd className="fd-content">{formatDateTime(f.firstTimeSeen)}</dd>
                        {f.fileName && (
                            <React.Fragment>
                                <dt className="fd-title">Filename at line:</dt>
                                <dd className="fd-content inline-flex">
                                    <span className="break-all">
                                        {f.fileName} {f.lineNumber! > 0 &&
                                    <React.Fragment><span title="Line number">: {f.lineNumber}</span><span
                                        title="Column">: {f.column}</span></React.Fragment>}
                                    </span>
                                </dd>
                            </React.Fragment>
                        )}
                        {stackTrace && stackTrace.length > 0 && (
                            <React.Fragment>
                                <dt className="col-span-12 lg:col-span-3 2xl:col-span-2">Stacktrace end:</dt>
                                <dd className="col-span-12 lg:col-span-9 2xl:col-span-10 inline-flex break-all  ">
                                    {toStackLine(stackTrace[0])}
                                </dd>
                            </React.Fragment>
                        )}
                    </dl>
                </div>
            </section>

            {stackTrace && (
                <ShrinkExpandFlatDetails id={f.flatId} title={'Stacktrace'} defaultExpanded={stackTrace?.length < 10}>
                    <FlatDetailsStackTrace stackTrace={stackTrace}/>
                </ShrinkExpandFlatDetails>
            )}

            {f.withStructuralData && <FlatDetailsStructuralData f={f} state={state}/>}

            {jsLogType && (
                <ShrinkExpandFlatDetails
                    id={f.flatId}
                    title={'Page Urls'}
                    defaultExpanded={false}
                    onExpand={() => state.loadPageUrls()}
                    loading={state.pageUrlsLoading}>
                    {state.pageUrls?.length === 0 && <span>No registered page urls</span>}
                    <table>
                        <tbody>
                        {state.pageUrls?.map((p) => (
                            <tr className="break-all hover:text-m-gray-6" key={'pagecount' + p.key}>
                                <td>
                                    <a href={p.url!} title={p.url!} target="_blank"
                                       rel="noopener noreferrer">{p.url}</a>
                                    &nbsp;
                                </td>
                                <td style={{width: '70px'}} className="text-right">
                                    <span title="Count" className="badge mr-2 right-0">
                                        {p.count}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {state.pageUrlsPage > 1 && <Button onClick={() => state.loadPageUrlsPreviousPage()} className="btn-primary mr-2">Previous</Button>}
                    {state.pageUrls?.length === 10 && <Button onClick={() => state.loadPageUrlsNextPage()} className="btn-primary">Show more</Button>}
                </ShrinkExpandFlatDetails>
            )}

            {jsLogType && <FlatDetailsDevices f={f} state={state}/>}

            <ShrinkExpandFlatDetails
                id={f.flatId}
                title={'Stats'}
                defaultExpanded={false}
                onExpand={() => state.loadStatsInfo()}
                loading={state.statsLoading}>
                <FlatStatsGraph data={f.stats}/>
            </ShrinkExpandFlatDetails>

            {f.info && (
                <ShrinkExpandFlatDetails id={f.flatId} title={'Info'}>
                    <ul>
                        {Object.keys(f.info).map((i) => {
                            return (
                                <li key={'info' + i}>
                                    {i}: {f!.info![i]}
                                </li>
                            );
                        })}
                    </ul>
                </ShrinkExpandFlatDetails>
            )}
            <ShrinkExpandFlatDetails id={f.flatId} title={'Notes'}>
                <div className="flex flex-wrap">
                    <textarea
                        placeholder="Write notes here..."
                        rows={4}
                        value={f.notes ?? ''}
                        className="form-control"
                        onChange={(e) => state.setNotes(e.target.value)}
                    />
                    <Button className="btn-primary ml-auto mt-6" onClick={() => state.saveNotes()}>
                        Save note
                    </Button>
                </div>
            </ShrinkExpandFlatDetails>
        </div>
    );
});
